<template>
  <transition name="fade">
    <div v-if="show" class="stm-backdrop" @click.self="cerrar">
      <div class="stm-dialog">
        <div class="stm-header">
          <div class="stm-header-left">
            <div class="stm-icon-wrap">
              <svg width="22" height="22" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M10 28v-4a14 14 0 0 1 28 0v4" stroke="#01662E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 28h4a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-8z" fill="#01662E"/>
                <path d="M38 28h-4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2v-8z" fill="#01662E"/>
                <path d="M32 38a4 4 0 0 1-4 4h-4" stroke="#01662E" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div>
              <h3 class="stm-title">Abrir ticket de soporte</h3>
              <p class="stm-subtitle">Recibirás respuesta por correo dentro del tiempo indicado.</p>
            </div>
          </div>
          <button class="stm-close" @click="cerrar" aria-label="Cerrar">&times;</button>
        </div>

        <div class="stm-body" v-if="!ticketCreado">
          <ticket-topic-line-picker
            :topics="topics"
            :topic.sync="form.topic"
            :line.sync="form.linea_negocio"
            :loading="cargandoConfig"
          />

          <ticket-priority-picker v-model="form.priority" :quota="priorityQuota" />

          <div v-if="!isMaterialsOrder" class="stm-field">
            <label>Asunto</label>
            <input v-model="form.asunto" type="text" class="stm-input" maxlength="200" placeholder="Resume en una línea qué pasó" />
          </div>

          <materials-order-picker v-if="isMaterialsOrder" v-model="form.items" :business-line="form.linea_negocio" @catalog-loaded="materialsCatalogItems = $event" />

          <div class="stm-field">
            <label>{{ descriptionField.label }}</label>
            <textarea v-model="form.descripcion" class="stm-input" rows="5" :maxlength="descriptionField.maxLength" :placeholder="descriptionField.placeholder"></textarea>
            <div class="stm-helper">
              <span>{{ form.descripcion.trim().length }} / {{ descriptionField.maxLength }}</span>
            </div>
          </div>

          <ticket-attachments-field v-model="adjuntos" />

          <div v-if="error" class="stm-alert stm-alert--error">{{ error }}</div>

          <div class="stm-actions" :class="{ 'stm-actions--sticky': isMaterialsOrder }">
            <materials-order-summary v-if="isMaterialsOrder" :items="materialsCatalogItems" :selection="form.items" />
            <button class="stm-btn stm-btn--ghost" @click="cerrar">Cancelar</button>
            <button class="stm-btn stm-btn--primary" :disabled="!formValido || creando" @click="enviar">
              <span v-if="creando">Enviando…</span>
              <span v-else>Enviar ticket</span>
            </button>
          </div>
        </div>

        <div v-else class="stm-body stm-success">
          <div class="stm-success-icon">✓</div>
          <h4>Ticket creado</h4>
          <p class="stm-success-id">{{ ticketCreado.ticket_id }}</p>
          <p class="stm-success-msg">Recibirás respuesta antes de <strong>{{ formatoFecha(ticketCreado.fecha_limite_resolucion) }}</strong>.</p>
          <button class="stm-btn stm-btn--primary" @click="cerrar">Cerrar</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import MaterialsOrderPicker from './MaterialsOrderPicker.vue';
import MaterialsOrderSummary from './MaterialsOrderSummary.vue';
import TicketAttachmentsField from './TicketAttachmentsField.vue';
import TicketPriorityPicker from './TicketPriorityPicker.vue';
import TicketTopicLinePicker from './TicketTopicLinePicker.vue';
import { isOrderReadyToSend } from './materialsOrderBoxes';
import { MATERIALS_ORDER_NOTE_FIELD, MATERIALS_ORDER_TOPIC, isBusinessLineRequired, subjectForTopic } from './materialsOrderSelection';
import { TICKET_PRIORITY_MEDIUM } from '@/utils/ticketStates';

const SUBJECT_MIN_LENGTH = 5;
const DESCRIPTION_MIN_LENGTH = 10;

const DESCRIPTION_FIELD = {
  label: 'Descripción',
  maxLength: 5000,
  placeholder: 'Cuenta con detalle qué reportas, cuándo pasó, contexto que ayude a resolverlo más rápido.',
};

const emptyForm = () => ({
  topic: '',
  linea_negocio: '',
  priority: TICKET_PRIORITY_MEDIUM,
  asunto: '',
  descripcion: '',
  items: {},
});

export default {
  name: 'SupportTicketModal',
  components: { MaterialsOrderPicker, MaterialsOrderSummary, TicketAttachmentsField, TicketPriorityPicker, TicketTopicLinePicker },
  props: {
    show: { type: Boolean, default: false },
  },
  data() {
    return {
      form: emptyForm(),
      adjuntos: [],
      materialsCatalogItems: [],
    };
  },
  computed: {
    config() { return this.$store.getters['tickets/configuracionTickets']; },
    cargandoConfig() { return this.$store.getters['tickets/ticketsCargandoConfig']; },
    creando() { return this.$store.getters['tickets/ticketsCreando']; },
    error() { return this.$store.getters['tickets/ticketsError']; },
    ticketCreado() { return this.$store.getters['tickets/ticketRecienCreado']; },
    topics() { return (this.config && this.config.temas) || []; },
    priorityQuota() { return (this.config && this.config.priority_quota) || null; },
    isMaterialsOrder() { return this.form.topic === MATERIALS_ORDER_TOPIC; },
    descriptionField() { return this.isMaterialsOrder ? MATERIALS_ORDER_NOTE_FIELD : DESCRIPTION_FIELD; },
    submittedSubject() { return subjectForTopic(this.form.topic, this.form.asunto); },
    hasRequiredDetail() {
      if (this.isMaterialsOrder) return isOrderReadyToSend(this.materialsCatalogItems, this.form.items);
      return this.form.descripcion.trim().length >= DESCRIPTION_MIN_LENGTH;
    },
    formValido() {
      return this.form.topic && (this.form.linea_negocio || !isBusinessLineRequired(this.form.topic)) && this.form.priority
        && this.submittedSubject.trim().length >= SUBJECT_MIN_LENGTH
        && this.hasRequiredDetail;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.$store.dispatch('tickets/resetTicket');
        this.$store.dispatch('tickets/cargarConfiguracion', { force: true });
      }
    },
  },
  methods: {
    async enviar() {
      try {
        await this.$store.dispatch('tickets/crearTicket', { ...this.form, asunto: this.submittedSubject, adjuntos: this.adjuntos });
      } catch (_) { /* error in store */ }
    },
    cerrar() {
      this.$emit('update:show', false);
      this.form = emptyForm();
      this.adjuntos = [];
    },
    formatoFecha(iso) {
      if (!iso) return '';
      return new Date(iso).toLocaleString('es-PE', { dateStyle: 'medium', timeStyle: 'short' });
    },
  },
};
</script>

<style scoped>
.stm-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 25, 20, 0.55);
  backdrop-filter: blur(3px);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.stm-dialog {
  background: #fff;
  border-radius: 18px;
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  box-shadow:
    0 30px 80px -10px rgba(0, 0, 0, 0.35),
    0 8px 20px -5px rgba(0, 0, 0, 0.08);
}

/* Header */
.stm-header {
  padding: 24px 28px 18px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 1px solid #eef1ef;
}
.stm-header-left {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.stm-icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e6f4ec 0%, #d3ecde 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.stm-title {
  margin: 0 0 3px;
  color: #01662E;
  font-size: 19px;
  font-weight: 700;
  letter-spacing: -0.2px;
}
.stm-subtitle {
  margin: 0;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
}
.stm-close {
  background: #f4f5f4;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  font-size: 18px;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
  padding: 0;
  margin-left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease;
  flex-shrink: 0;
}
.stm-close:hover { background: #e9ebe9; color: #2d2d2d; }

/* Body */
.stm-body {
  padding: 22px 28px 26px;
}
.stm-field {
  margin-bottom: 16px;
}
.stm-field label {
  font-weight: 600;
  font-size: 12.5px;
  color: #374151;
  margin-bottom: 6px;
  display: block;
  letter-spacing: 0.1px;
}
.stm-input {
  width: 100%;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  padding: 10px 12px;
  font-size: 14px;
  background: #fff;
  color: #1f2937;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
}
.stm-input:hover:not(:disabled) {
  border-color: #d1d5db;
}
.stm-input:focus {
  border-color: #01662E;
  outline: none;
  box-shadow: 0 0 0 3px rgba(1, 102, 46, 0.12);
}
.stm-input:disabled {
  background: #f9fafb;
  cursor: not-allowed;
  color: #9ca3af;
}
textarea.stm-input {
  resize: vertical;
  min-height: 110px;
  line-height: 1.55;
}
.stm-helper {
  display: block;
  text-align: right;
  font-size: 11.5px;
  color: #9ca3af;
  margin-top: 4px;
}

/* Alert */
.stm-alert {
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  margin-top: 12px;
}
.stm-alert--error {
  background: #fef2f2;
  color: #b91c1c;
  border-left: 3px solid #b91c1c;
}

/* Buttons */
.stm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid #eef1ef;
}
.stm-actions--sticky {
  position: sticky;
  bottom: 0;
  flex-wrap: wrap;
  margin-bottom: -26px;
  padding-bottom: 26px;
  background: #fff;
  box-shadow: 0 -12px 16px -14px rgba(15, 25, 20, 0.22);
}
.stm-btn {
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;
}
.stm-btn--ghost {
  background: transparent;
  color: #6b7280;
}
.stm-btn--ghost:hover {
  background: #f4f5f4;
  color: #2d2d2d;
}
.stm-btn--primary {
  background: #01662E;
  color: #fff;
  box-shadow: 0 2px 6px rgba(1, 102, 46, 0.25);
}
.stm-btn--primary:hover:not(:disabled) {
  background: #008846;
  box-shadow: 0 4px 12px rgba(1, 102, 46, 0.32);
}
.stm-btn--primary:disabled {
  background: #d1d5db;
  color: #9ca3af;
  box-shadow: none;
  cursor: not-allowed;
}

/* Success */
.stm-success {
  text-align: center;
  padding: 40px 28px 32px;
}
.stm-success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #01662E;
  color: #fff;
  font-size: 28px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  box-shadow: 0 6px 18px rgba(1, 102, 46, 0.3);
}
.stm-success h4 {
  margin: 0 0 6px;
  color: #1f2937;
  font-size: 18px;
  font-weight: 600;
}
.stm-success-id {
  color: #01662E;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.5px;
  margin: 0 0 14px;
}
.stm-success-msg {
  color: #6b7280;
  font-size: 14px;
  margin: 0 0 24px;
  line-height: 1.5;
}

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.22s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.fade-enter-active .stm-dialog, .fade-leave-active .stm-dialog {
  transition: transform 0.25s cubic-bezier(.2, .8, .2, 1);
}
.fade-enter .stm-dialog, .fade-leave-to .stm-dialog {
  transform: translateY(12px) scale(0.98);
}
</style>
